import jsPDF from 'jspdf';
import 'svg2pdf.js';

export const downloadPdf = async (activities, labels, additionalParameters) => await getPdf(activities, labels, additionalParameters, true);

export const getPdf = async (activities, labels, additionalParameters, save = false) => {
  const doc = new jsPDF({ orientation: 'l' });

  const withPrId = additionalParameters.withPrId;
  const withUserSignature = additionalParameters.withUserSignature;

  let headers = [
    {
      label: 'data',
      widthPerc: 7,
    },
    {
      label: 'activity',
      widthPerc: withPrId ? 20 : 25,
    },
    {
      label: 'fromHours',
      widthPerc: 7,
    },
    {
      label: 'toHours',
      widthPerc: 7,
    },
    {
      label: 'total',
      widthPerc: 7,
    },
    {
      label: 'signUserInCharge',
      widthPerc: withPrId ? 31 : 33,
    },
    {
      label: 'operator',
      widthPerc: 11,
    },
  ];

  if (withPrId) {
    headers.splice(2, 0, { label: 'siat_practice', widthPerc: 7 });
  }

  if (withUserSignature) {
    const signUserInCharge = headers.find((header) => header.label === 'signUserInCharge');
    const signWidth = signUserInCharge.widthPerc / 2;
    headers.splice(6, 0, { label: 'userSignature', widthPerc: signWidth });
    signUserInCharge.widthPerc = signWidth;
  }

  const width = 297;
  const height = 210;
  const margin = 10;
  const offsetX = 1;
  let currentHeigth = 7;
  doc.setFontSize(10);
  let page = 0;
  let cumulativeX = 0;
  const width_table = width - 2 * margin;
  const now = new Date();
  const dateReport = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  const STARTING_HEIGHT = 29;
  const TOTAL_HEIGHT = 7;
  const MAX_NUMBER_SIGN_PER_PAGE = 5;
  let signOnPage = 0;
  const logo = additionalParameters.logo;
  const LINE_WIDTH = 0.3;
  const rowHeightPadding = 4;
  const resetCurrentHeigth = STARTING_HEIGHT + 7;

  const getRowHeight = (doc, activity, headers, width_table, offsetX) => {
    let maxHeight = 0;

    headers.forEach((header) => {
      let text = activity[header.label] ?? '';
      let cellWidth = (width_table * header.widthPerc) / 100 - 2 * offsetX;

      if (header.label === 'activity') {
        const textDimensions = doc.getTextDimensions(text, { maxWidth: cellWidth });
        maxHeight = Math.max(maxHeight, textDimensions.h);
      } else {
        maxHeight = Math.max(maxHeight, 30);
      }
    });

    return maxHeight + rowHeightPadding;
  };

  const addActivityOnHeader = async (headers, activity) => {
    await asyncForEach(headers, async (header) => {
      switch (header.label) {
        case 'activity':
          text = activity[header.label];
          doc.text(text, cumulativeX + margin + offsetX, currentHeigth, { maxWidth: (width_table * header.widthPerc) / 100 - 2 * offsetX });
          break;
        case 'signUserInCharge':
          if (activity.signIsImage) {
            if (activity.rotateSign) {
              await doc.svg(activity[header.label], {
                x: cumulativeX + margin + offsetX,
                y: currentHeigth,
                height: withUserSignature ? 45 : 90,
                width: withUserSignature ? 15 : 30,
              });
            } else {
              await doc.svg(activity[header.label], {
                x: cumulativeX + margin + offsetX,
                y: currentHeigth,
                width: withUserSignature ? 45 : 90,
                height: withUserSignature ? 15 : 30,
              });
            }
          } else {
            text = activity[header.label];
            doc.text(text, cumulativeX + margin + offsetX, currentHeigth, {
              maxWidth: (width_table * header.widthPerc) / 100 - 2 * offsetX,
            });
          }
          break;
        case 'userSignature':
          if (activity.userSignIsImage) {
            if (activity.userRotateSign) {
              await doc.svg(activity[header.label], {
                x: cumulativeX + margin + offsetX,
                y: currentHeigth,
                height: withUserSignature ? 45 : 90,
                width: withUserSignature ? 15 : 30,
              });
            } else {
              await doc.svg(activity[header.label], {
                x: cumulativeX + margin + offsetX,
                y: currentHeigth,
                width: withUserSignature ? 45 : 90,
                height: withUserSignature ? 15 : 30,
              });
            }
          } else {
            text = activity[header.label];
            doc.text(text, cumulativeX + margin + offsetX, currentHeigth, {
              maxWidth: (width_table * header.widthPerc) / 100 - 2 * offsetX,
            });
          }
          break;
        case 'total':
          text = timeConvert(activity[header.label]);
          doc.text(text, cumulativeX + margin + offsetX, currentHeigth);
          break;
        default:
          text = activity[header.label];
          doc.text(text, cumulativeX + margin + offsetX, currentHeigth);
          break;
      }
      cumulativeX += (width_table * header.widthPerc) / 100;
    });
  };

  const shouldGoToNextPage = (currentHeigth, rowHeight, signOnPage) => {
    return currentHeigth + rowHeight + margin > height || signOnPage === MAX_NUMBER_SIGN_PER_PAGE;
  };

  const addActivities = async (activities) => {
    await asyncForEach(activities, async (activity, index) => {
      if (currentCfGuest !== activity.cf) {
        if (currentCfGuest !== null) {
          addTotals(total, totalWeekday, totalHoliday);
        }
        addPage(index);
        totalWeekday = 0;
        totalHoliday = 0;
        total = 0;
        currentCfGuest = activity.cf;
        currentHeigth = resetCurrentHeigth;
      }

      if (activity.isHoliday) {
        totalHoliday += activity.total;
      } else {
        totalWeekday += activity.total;
      }

      cumulativeX = 0;
      text = '';

      const rowHeight = getRowHeight(doc, activity, headers, width_table, offsetX);

      if (shouldGoToNextPage(currentHeigth, rowHeight, signOnPage)) {
        addPage(index);
        currentHeigth = resetCurrentHeigth;
        cumulativeX = 0;
        signOnPage = 0;
      }

      await addActivityOnHeader(headers, activity);

      total += activity.total;
      signOnPage++;

      currentHeigth += rowHeight;
    });
  };

  const addHeader = (index) => {
    doc.setLineWidth(LINE_WIDTH);
    let h = 15;
    if (logo) {
      try {
        doc.addImage(logo, 'JPEG', 2, 2, 12, 12);
      } catch (error) {
        console.log('error on logo', error);
      }
    }

    let headerLabel = `${labels.titleReport}`;
    if (additionalParameters.month && additionalParameters.year) {
      headerLabel = `${headerLabel} - ${additionalParameters.month.toUpperCase()} / ${additionalParameters.year}`;
    } else if (additionalParameters.from && additionalParameters.to) {
      headerLabel = `${headerLabel} - ${labels.from} ${additionalParameters.from} ${labels.to} ${additionalParameters.to}`;
    }
    doc.text(headerLabel, margin + 12, 10);
    doc.text(dateReport, width - margin, 10, { align: 'right' });

    doc.rect(margin, h, width - 2 * margin, 7, 'S');
    h += 5;
    doc.text(`${activities[index].fullname} (${activities[index].cf})`, margin + offsetX, h);
    h += 2;
    doc.rect(margin, h, width - 2 * margin, 7, 'S');
    h += 7;
    cumulativeX = 0;
    headers.forEach((header, index) => {
      cumulativeX += (width_table * header.widthPerc) / 100;
      if (index + 1 !== headers.length) {
        doc.line(cumulativeX + margin, h - 7, cumulativeX + margin, h);
      }
    });

    cumulativeX = 0;
    headers.forEach((header) => {
      doc.text(labels[header.label], cumulativeX + margin + offsetX, h - 2);
      cumulativeX += (width_table * header.widthPerc) / 100;
    });
    page++;
    doc.text(`${labels.page}: ${page}`, width / 2, height - 5);
  };

  const addPage = (index) => {
    if (index !== 0) {
      drawTable();
      doc.addPage();
      signOnPage = 0;
    }
    addHeader(index);
  };

  const addTotals = (total, totalWeekday, totalHoliday) => {
    doc.setLineWidth(LINE_WIDTH);
    doc.rect(margin, height - margin - TOTAL_HEIGHT, width - 2 * margin, TOTAL_HEIGHT, 'S');

    const widthTotals = width - 2 * margin;
    const defaultSpace = 15;
    // Totale gg Feriali
    doc.text(labels.totalWeekday, margin + offsetX, height - margin - TOTAL_HEIGHT + 4);
    doc.text(timeConvert(totalWeekday), margin + offsetX + defaultSpace, height - margin - TOTAL_HEIGHT + 4);

    // Totale gg Festivi
    doc.text(labels.totalHoliday, widthTotals * 0.3 + offsetX, height - margin - TOTAL_HEIGHT + 4);
    doc.text(timeConvert(totalHoliday), widthTotals * 0.3 + offsetX + defaultSpace, height - margin - TOTAL_HEIGHT + 4);

    // Totale ore lavorate
    doc.text(labels.total, widthTotals * 0.6 + offsetX, height - margin - TOTAL_HEIGHT + 4);
    doc.text(timeConvert(total), widthTotals * 0.6 + offsetX + defaultSpace, height - margin - TOTAL_HEIGHT + 4);
  };

  const drawTable = () => {
    doc.rect(margin, STARTING_HEIGHT, width - 2 * margin, height - margin - STARTING_HEIGHT - TOTAL_HEIGHT, 'S');

    cumulativeX = 0;
    headers.forEach((header, index) => {
      cumulativeX += (width_table * header.widthPerc) / 100;
      if (index + 1 !== headers.length) {
        doc.line(cumulativeX + margin, STARTING_HEIGHT, cumulativeX + margin, height - margin - TOTAL_HEIGHT);
      }
    });
  };

  let currentCfGuest = null;
  let totalWeekday = 0;
  let totalHoliday = 0;
  let total = 0;
  let text = '';

  if (activities.length > 0) {
    await addActivities(activities);
    addTotals(total, totalWeekday, totalHoliday);
    drawTable();
  }
  if (save) {
    const now = new Date();
    let dateReport = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, 0)}${String(now.getDate()).padStart(2, 0)}`;
    dateReport += `_${String(now.getHours()).padStart(2, 0)}${String(now.getMinutes()).padStart(2, 0)}${String(now.getSeconds()).padStart(2, 0)}`;
    return doc.save(`Report_firme_${dateReport}.pdf`, { returnPromise: true });
  }
  return doc.output('bloburl', { filename: 'Report_firme' });
};

const timeConvert = (num) => {
  const hours = Math.floor(num / 60);
  const minutes = num % 60;
  return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
};

const asyncForEach = async (array, callback) => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
};
